import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { Http } from "../common/http";
import { isType } from "../common/utils";
import { SupportedLanguages } from "../i18n";
import { carbonSnapMocks } from "../mocks/carbonsnap/carbonSnapMocks";
import { registerMockInterceptor } from "../mocks/utils/mockInterceptor";
import { getCurrentAuthenticationToken } from "../state/auth";
import { getLocalPreferences } from "../state/preferences";

const requestLanguage = () => getLocalPreferences()?.language ?? SupportedLanguages.English;
const requestToken = () => getCurrentAuthenticationToken() ?? "";

const populateHeaders = (
	config: InternalAxiosRequestConfig,
	isAuthenticated: boolean = false
): InternalAxiosRequestConfig => {
	config.headers.set("Accept-Language", requestLanguage());

	if (isAuthenticated) {
		config.headers.Authorization = `Bearer ${requestToken()}`;
	}

	return config;
};

/**
 * authenticationAPI is the HTTP client that communicates with authentication APIs.
 */
export const authenticationAPI = axios.create({
	timeout: 150000,
	validateStatus: () => true,
	baseURL: import.meta.env.VITE_AUTH_URL,
	headers: { "Content-Type": "application/json" },
});

/**
 * postAuthenticationAPI is the HTTP client that communicates with token verification
 * and scope (or instance) selection APIs.
 */
export const postAuthenticationAPI = axios.create({
	timeout: 150000,
	validateStatus: () => true,
	baseURL: import.meta.env.VITE_AUTH_URL,
	headers: { Accept: "application/json" },
});

/**
 * greenRouterAPI is the HTTP client that communicates with GreenRouter APIs.
 */
export const greenRouterAPI = axios.create({
	timeout: 150000,
	validateStatus: () => true,
	baseURL: import.meta.env.VITE_GREEN_ROUTER_URL,
	headers: {
		Accept: "application/json",
		"Content-Type": "application/json",
	},
});

postAuthenticationAPI.interceptors.request.use((config) => populateHeaders(config, true));
authenticationAPI.interceptors.request.use((config) => populateHeaders(config));
greenRouterAPI.interceptors.request.use((config) => populateHeaders(config, true));

if (import.meta.env.VITE_MOCK_API === "true") {
	registerMockInterceptor(greenRouterAPI, carbonSnapMocks);
}

/**
 * Defines structure of an error message.
 */
export type ErrorResponse = Error & {
	/**
	 * The application optional error code.
	 */
	code?: number;

	/**
	 * The HTTP status code.
	 */
	httpCode: number;

	/**
	 * The temporary token that may be required for further actions.
	 */
	temporaryToken?: string;

	/**
	 * A [string, string] dictionary describing all additional errors.
	 */
	errors?: Record<string, string>;
};

/**
 * Returns a generic "reject" promise for the given error response.
 *
 * @param httpCode the HTTP status code
 * @returns        a rejection Promise
 */
export const httpRejectRequest = <T>(httpCode: number): Promise<T> => {
	let message: string;

	switch (httpCode) {
		case 401:
		case 403:
		case 404:
		case 422:
			message = `http/${httpCode}_message`;
			break;
		default:
			console.warn(
				`cannot produce an appropriate rejection promise for HTTP status code ${httpCode}, falling back to 422`
			);
			message = `http/${httpCode}_message`;
			break;
	}

	const error: ErrorResponse = { name: message, message, httpCode };
	return Promise.reject(error);
};

/**
 * Returns a "reject" promise for the given error response.
 * If "e" is not an AxiosError, this method will return the given status code or 422 (default).
 *
 * @param e               the original error HTTP status code
 * @param preferredStatus the status code to be returned if e is not an AxiosError
 *                        or as the preferred status code (overrides e.response.status,
 *                        defaults to 422)
 * @returns               a rejection Promise
 */
export const rejectRequest = <T>(e: any, preferredStatus?: number): Promise<T> => {
	const httpCode = preferredStatus ? preferredStatus : (e.status ?? Http.UnprocessableEntity);

	if (!isType<AxiosError>(e)) {
		return httpRejectRequest(httpCode);
	}

	const error = e as AxiosError;
	if (!error.response || !error.response.data) {
		return httpRejectRequest(httpCode);
	}

	const response = error.response.data as ErrorResponse;
	return Promise.reject({ ...response, httpCode });
};
